import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  ConditionalRenderer,
  ThemesDropdown,
  useLanguageTranslator,
} from "../../../../../components";
import { useCallback, useMemo } from "react";
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from "@mui/material";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useAuthSession } from "../../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../../components/@common/NotificationsProvider";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApolloQuery } from "../../../../../hooks";

const CREATE_INDICATORS = gql`
  mutation CreateIndicator($input: IndicatorCreateInput!) {
    indicatorCreate(input: $input) {
      clientMutationId
      indicator {
        question
        label
        indicatorCategoryId
        themeId
        ratingType
        createdAt
        id
        updatedAt
      }
    }
  }
`;

const GET_INDICATOR = gql`
  query Indicator($id: ID!) {
    indicator(id: $id) {
      question
      label
      indicatorCategoryId
      themeId
      ratingType
      createdAt
      id
      updatedAt
    }
  }
`;

const UPDATE_INDICATOR = gql`
  mutation UpdateIndicator(
    $id: ID!
    $question: String!
    $label: String!
    $indicatorCategoryId: Int!
    $themeId: Int!
    $ratingType: String!
  ) {
    indicatorUpdate(
      input: {
        id: $id
        indicatorInput: {
          question: $question
          label: $label
          indicatorCategoryId: $indicatorCategoryId
          themeId: $themeId
          ratingType: $ratingType
        }
      }
    ) {
      clientMutationId
      indicator {
        createdAt
        id
        indicatorCategoryId
        label
        peopleSource
        policySource
        practiceSource
        question
        ratingType
        themeId
        updatedAt
      }
    }
  }
`;

const GET_INDICATOR_CATEGORIES = gql`
  query GetIndicatorCategories {
    indicatorCategories {
      id
      name
    }
  }
`;

const ratingTypeItems = [
  {
    name: "Percentage",
    value: "percentage",
  },
  {
    name: "Boolean",
    value: "boolean",
  },
  {
    name: "String",
    value: "string",
  },
  {
    name: "Amount",
    value: "amount",
  },
];

function IndicatorAction({ isUpdate = false }: any) {
  const { translator, currentLanguage } = useLanguageTranslator();
  const { user } = useAuthSession() as any;
  const notify = useNotify();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const indicator_id = searchParams.get("id");

  const { data: indicatorCategoriesData }: any = useApolloQuery({
    queryDocument: GET_INDICATOR_CATEGORIES,
    isPrivateCall: true,
  });

  const { data: indicatorData } = useQuery(GET_INDICATOR, {
    variables: { id: indicator_id },
    skip: !indicator_id,
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const initialValues: any = useMemo(() => {
    if (!indicatorData?.indicator) return undefined;

    const { question, label, indicatorCategoryId, themeId, ratingType } =
      indicatorData?.indicator;

    return {
      question: question,
      label: label,
      category: indicatorCategoryId,
      theme: themeId,
      rating_type: ratingType,
    };
  }, [indicatorData]);

  const form = useForm<any>({
    defaultValues: {
      question: "",
      label: "",
      category: null,
      theme: null,
      rating_type: null,
    },
    values: initialValues,
  });

  const [createIndicator, { loading, error }] = useMutation(CREATE_INDICATORS, {
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [updateIndicator] = useMutation(UPDATE_INDICATOR, {
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const handleFinish = useCallback(async (data: any, actionType: any) => {
    if (actionType === "update") {
      try {
        const response = await updateIndicator({
          variables: {
            id: Number(indicator_id),
            question: data?.question,
            label: data?.label,
            indicatorCategoryId: Number(data?.category),
            themeId: Number(data?.theme),
            ratingType: data?.rating_type,
          },
        });
        if (response.data) {
          notify.success(`Indicator Updated successfuly!`);
          setTimeout(() => {
            navigate("/admin/indicators");
            return;
          }, 1000);
        }
      } catch (err) {}
      return;
    }
    const response = await createIndicator({
      variables: {
        input: {
          indicatorInput: {
            question: data?.question,
            label: data?.label,
            indicatorCategoryId: Number(data?.category),
            themeId: Number(data?.theme),
            ratingType: data?.rating_type,
          },
        },
      },
    });
    try {
      if (actionType === "save") {
        if (response.data) {
          notify.success(`Indicator Created successfuly!`);
          setTimeout(() => {
            navigate("/admin/indicators");
            return;
          }, 1000);
        }
      } else if (actionType === "saveAndAddNew") {
        if (response.data) {
          notify.success(`Indicator Created successfuly!`);
          setTimeout(() => {
            form?.reset();
            return;
          }, 1000);
        }
      }
    } catch (err) {}
  }, []);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit((data) => handleFinish(data, "save"))}>
        <Grid
          container
          px={4}
          sx={{ backgroundColor: "#ffffff", borderRadius: "10px" }}
          spacing={2}
          pt={2}
        >
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="question"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("Question")}</FormLabel>
                  <OutlinedInput
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      form?.trigger(field.name);
                    }}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="label"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("Label")}</FormLabel>
                  <OutlinedInput
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      form?.trigger(field.name);
                    }}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="category"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>
                    {translator("dashboardTableHead.Category")}
                  </FormLabel>
                  <Select {...field} displayEmpty>
                    {indicatorCategoriesData?.indicatorCategories?.map(
                      (op: any, i: any) => (
                        <MenuItem key={`${op.value}-${i}`} value={op.id}>
                          {op.name}
                        </MenuItem>
                      )
                    )}
                  </Select>
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="theme"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>{translator("Theme")}</FormLabel>
                  <ThemesDropdown {...field} />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Controller
              name="rating_type"
              rules={{ required: "Required!" }}
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  fullWidth
                  required
                  error={!!error}
                  disabled={false}
                  sx={{ margin: "0px" }}
                >
                  <FormLabel>
                    {translator("dashboardTableHead.Rating type")}
                  </FormLabel>
                  <Select {...field} displayEmpty>
                    {ratingTypeItems?.map((op: any, i: any) => (
                      <MenuItem key={`${op.value}-${i}`} value={op.value}>
                        {op.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
        <Stack
          sx={{ backgroundColor: "#ffffff" }}
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
          px={4}
          py={4}
        >
          <Link
            to="/admin/indicators"
            onClick={() => form?.reset()}
            style={{
              backgroundColor: "#e0e0e0",
              padding: "8px 12px",
              color: "#989C9F",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {translator("Cancel")}
          </Link>
          <Button
            type="button"
            onClick={form.handleSubmit((data) =>
              handleFinish(data, isUpdate ? "update" : "save")
            )}
            style={{
              backgroundColor: "#00AAB0",
              padding: "8px 12px",
              color: "white",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {isUpdate
              ? translator("generic.Update")
              : translator("generic.Save")}
          </Button>
          <ConditionalRenderer condition={!isUpdate}>
            <Button
              type="button"
              onClick={form.handleSubmit((data) =>
                handleFinish(data, "saveAndAddNew")
              )}
              style={{
                backgroundColor: "#F6A000",
                padding: "8px 12px",
                color: "white",
                borderRadius: "10px",
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              {translator("generic.Save")} & {translator("generic.Add New")}
            </Button>
          </ConditionalRenderer>
        </Stack>
      </form>
    </FormProvider>
  );
}

export { IndicatorAction };
